import { useMemo, useState } from 'react';

import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { DateRangePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { RangeInput } from '@mui/lab/DateRangePicker/RangeTypes';
import {
  IconButton,
  Typography,
  TextField,
  Box,
  FormControlLabel,
  FormGroup,
  Switch,
} from '@mui/material';
import { parse } from 'date-fns';

import { InputClearButton } from 'components';
import { DATE_FORMAT } from 'config';
import { safeFormatDate } from 'helpers';
import { OptionalCallback, UserTableGlobalFilterOptions } from 'types';

import { SidebarWrapper } from './styledComponents';

interface SidebarProps {
  setFilters: (arg: OptionalCallback<UserTableGlobalFilterOptions>) => void;
  filters: UserTableGlobalFilterOptions;
}

export const UserTableSidebar: React.FC<SidebarProps> = ({
  setFilters,
  filters,
}) => {
  const [expanded, setExpanded] = useState(true);
  const { email, first_name, last_name, last_login_min, last_login_max } =
    filters ?? {};
  const dateLastLoggedInValue: RangeInput<string> = useMemo(
    () => [
      last_login_min ? parse(last_login_min, DATE_FORMAT, new Date()) : null,
      last_login_max ? parse(last_login_max, DATE_FORMAT, new Date()) : null,
    ],
    [last_login_min, last_login_max],
  );

  return (
    <SidebarWrapper expanded={expanded}>
      <header>
        {expanded && <Typography variant='h5'>Filter</Typography>}
        <IconButton
          onClick={() => setExpanded(!expanded)}
          key='expand'
          role='button'
          aria-label='expand and close filter'
          aria-expanded
        >
          {expanded ? (
            <KeyboardArrowLeft fontSize='medium' />
          ) : (
            <KeyboardArrowRight fontSize='medium' />
          )}
        </IconButton>
      </header>

      {expanded && (
        <section>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormGroup>
              <FormControlLabel
                sx={{ mb: 2, ml: 0.1 }}
                control={
                  <Switch
                    checked={filters.is_cognito !== undefined}
                    onChange={(e, checked) => {
                      setFilters((prev) => ({
                        ...prev,
                        is_cognito: checked ? false : undefined,
                      }));
                    }}
                    size='small'
                    color='warning'
                  />
                }
                label='Show all users'
              />
            </FormGroup>
            <TextField
              fullWidth
              size='small'
              label='Email'
              value={email ?? ''}
              onChange={({ target: { value } }) => {
                setFilters((prev) => ({
                  ...prev,
                  email: value ? value : undefined,
                }));
              }}
            />
            <TextField
              fullWidth
              size='small'
              label='First Name'
              value={first_name ?? ''}
              onChange={({ target: { value } }) => {
                setFilters((prev) => ({
                  ...prev,
                  first_name: value ? value : undefined,
                }));
              }}
            />
            <TextField
              fullWidth
              size='small'
              label='Last Name'
              value={last_name ?? ''}
              onChange={({ target: { value } }) => {
                setFilters((prev) => ({
                  ...prev,
                  last_name: value ? value : undefined,
                }));
              }}
            />
            <Typography
              sx={{ mt: 2 }}
              variant='body2'
              id='contract-select-label'
            >
              Last Login
            </Typography>
            <DateRangePicker
              mask='__/__/____'
              inputFormat='dd/MM/yyyy'
              calendars={1}
              onChange={(daterange) => {
                setFilters((prev) => ({
                  ...prev,
                  last_login_min: daterange[0]
                    ? safeFormatDate(daterange[0], DATE_FORMAT)
                    : undefined,
                  last_login_max: daterange[1]
                    ? safeFormatDate(daterange[1], DATE_FORMAT)
                    : undefined,
                }));
              }}
              value={dateLastLoggedInValue}
              renderInput={(startProps, endProps) => (
                <Box
                  aria-label='Filter by last login start and end date'
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    size='small'
                    {...startProps}
                    style={{ marginTop: 5 }}
                    InputProps={{
                      'aria-label': 'Last login start date',
                      style: { fontSize: 12 },
                      endAdornment: (
                        <InputClearButton
                          buttonProps={{ 'aria-label': 'Clear selection' }}
                          visible={!!last_login_min}
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              last_login_min: undefined,
                            }))
                          }
                        />
                      ),
                    }}
                  />
                  <Box sx={{ mx: 1 }}>
                    <Typography variant='body2'>to</Typography>{' '}
                  </Box>
                  <TextField
                    size='small'
                    {...endProps}
                    style={{ marginTop: 0 }}
                    InputProps={{
                      'aria-label': 'Last login end date',
                      style: { fontSize: 12 },
                      endAdornment: (
                        <InputClearButton
                          buttonProps={{ 'aria-label': 'Clear selection' }}
                          visible={!!last_login_max}
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              last_login_max: undefined,
                            }))
                          }
                        />
                      ),
                    }}
                  />
                </Box>
              )}
              clearable
            />
          </LocalizationProvider>
        </section>
      )}
    </SidebarWrapper>
  );
};
